import type { ReactNode } from "react";
import type { ColumnsType } from "antd/es/table";

import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import { getColumnTitle } from "@/utils/tableHelpers";
import type { EnrollmentTargetRow } from "./enrollmentTarget.types";

function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

const FIELD_TITLES: Record<string, string> = {
  month: "Month",
  sponsor_plan: "Planned cumulative enrolment",
  rationale: "Rationale",
  created_at: "Created On",
  created_by_name: "Created By",
  last_modified_at: "Last Updated",
  last_modified_by_name: "Updated By",
};

function getFieldTitle(field: string): string {
  return FIELD_TITLES[field] || getColumnTitle(field);
}

const FIELD_WIDTHS: Record<string, number> = {
  month: 110,
  sponsor_plan: 230,
  rationale: 260,
};

function getFieldWidth(field: string): number {
  return (
    FIELD_WIDTHS[field] ?? Math.max(100, 15 * (getFieldTitle(field).length + 1))
  );
}

function getFieldRenderer(
  field: string,
  decreasingMonths: string[],
): (text: unknown, record: EnrollmentTargetRow) => ReactNode {
  const renderMap: Record<
    string,
    (text: unknown, record: EnrollmentTargetRow) => ReactNode
  > = {
    month: (value: unknown) => value as string,
    sponsor_plan: (value: unknown, record) => {
      const decreasing = decreasingMonths.includes(record.month.slice(0, 7));
      return (
        <span
          className={
            decreasing
              ? "font-medium text-[#B91C1C]"
              : "font-medium text-[#06709B]"
          }
          title={
            decreasing
              ? "Lower than the month before — a cumulative total cannot fall."
              : undefined
          }
        >
          {value as number}
        </span>
      );
    },
    created_at: (value: unknown) => (
      <span className="text-[#374151]">{formatTimestamp(value as string)}</span>
    ),
    created_by_name: (value: unknown) =>
      value ? (
        <AvatarWithInitials
          name={value as string}
          avatarSize={24}
          avatarFontSize={11}
          labelFontSize={13}
        />
      ) : (
        <span className="text-[#8AA0A9]">—</span>
      ),
    last_modified_at: (value: unknown, record) => {
      // Stamped by auto_now on every save, including the row's own creation — showing
      // it when nothing has actually changed since created_at reads as a phantom edit.
      if (record.last_modified_at === record.created_at) {
        return <span className="text-[#8AA0A9]">—</span>;
      }
      return (
        <span className="text-[#374151]">
          {formatTimestamp(value as string)}
        </span>
      );
    },
    last_modified_by_name: (value: unknown, record) =>
      value && record.last_modified_at !== record.created_at ? (
        <AvatarWithInitials
          name={value as string}
          avatarSize={24}
          avatarFontSize={11}
          labelFontSize={13}
        />
      ) : (
        <span className="text-[#8AA0A9]">—</span>
      ),
  };

  const defaultRenderer = (text: unknown) =>
    text === null || text === undefined || text === "" ? (
      <span className="text-[#8AA0A9]">—</span>
    ) : (
      (text as ReactNode)
    );

  return renderMap[field] || defaultRenderer;
}

const omittedFields = ["id"];

export function createEnrollmentTargetColumns(
  fields: string[],
  lockedColumns: number,
  decreasingMonths: string[],
): ColumnsType<EnrollmentTargetRow> {
  return fields
    .filter((field) => !omittedFields.includes(field))
    .map((field, index) => ({
      title: getFieldTitle(field),
      dataIndex: field,
      key: field,
      width: getFieldWidth(field),
      fixed: index < lockedColumns ? ("left" as const) : undefined,
      render: (text: unknown, record: EnrollmentTargetRow) =>
        getFieldRenderer(field, decreasingMonths)(text, record),
    }));
}
